// Text-overlay template registry (fusion flow) — auto-loads every
// *.json file in THIS directory via Vite's import.meta.glob and exposes
// them keyed by id.
//
// SEPARATE from json-templates/registry.ts on purpose: these are
// text-ONLY templates (heading/subhead/cta over a transparent canvas)
// consumed by the recipe-fusion render path, not the Social Post route.
// Keeping them in a sibling dir means the Social Post picker's glob
// (json-templates/*.json) never picks them up, and vice versa.
//
// Authoring: design a frame in Figma → plugin "Text overlay" mode →
// Send to dev → lands here as <id>.json (see README.md for the shape).
//
// Consumers (wired in tasks #39/#40):
//   - the fusion vision-picker enumerates getTextTemplateList() to ask
//     gpt-4.1-mini which layout fits the rendered scene
//   - the overlay engine loads getTextTemplate(id) to composite the copy

import type { JsonTemplate } from '../json-templates/types';

// Eager so the picker has the full list synchronously. Empty dir → {}.
const modules = import.meta.glob<JsonTemplate>('./*.json', {
  eager: true,
  import: 'default',
});

/** Map of text-template id → parsed JsonTemplate config. */
export const TEXT_TEMPLATES: Record<string, JsonTemplate> = (() => {
  const out: Record<string, JsonTemplate> = {};
  for (const path in modules) {
    const tpl = modules[path];
    if (!tpl || typeof tpl !== 'object' || !tpl.id) {
      console.warn(`[text-templates] Skipping ${path}: missing or invalid id field`);
      continue;
    }
    if (out[tpl.id]) {
      console.warn(`[text-templates] Duplicate id "${tpl.id}" in ${path}`);
    }
    out[tpl.id] = tpl;
  }
  return out;
})();

/** Array of registered text-template ids. */
export function getTextTemplateIds(): string[] {
  return Object.keys(TEXT_TEMPLATES);
}

/** Compact list for the vision picker: id + name + canvas aspect, so the
 *  model can choose a layout (and prefer aspect-matched ones) without
 *  being handed the full element tree. */
export function getTextTemplateList(): Array<{
  id: string;
  name: string;
  width: number;
  height: number;
}> {
  return Object.values(TEXT_TEMPLATES).map((t) => ({
    id: t.id,
    name: t.name,
    width: t.canvas?.width ?? 1024,
    height: t.canvas?.height ?? 1024,
  }));
}

/** Lookup by id. Returns undefined if no text template with that id. */
export function getTextTemplate(id: string): JsonTemplate | undefined {
  return TEXT_TEMPLATES[id];
}

/** True if the id refers to a text-overlay template. */
export function isTextTemplate(id: string): boolean {
  return id in TEXT_TEMPLATES;
}
